import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  Switch,
} from 'react-native';
import { theme } from '../../theme/theme';
import { CloseIcon } from '../icons';

export interface SearchFilters {
  category?: string;
  isForSale?: boolean;
  priceMin?: number;
  priceMax?: number;
  medium?: string;
  sortBy?: 'created_at' | 'likes_count' | 'views_count' | 'price' | 'title';
  sortOrder?: 'ASC' | 'DESC';
  tags?: string[];
}

interface FilterModalProps {
  visible: boolean;
  onClose: () => void;
  onApply: (filters: SearchFilters) => void;
  initialFilters?: SearchFilters;
}

const CATEGORIES = [
  { value: '', label: '全部分类' },
  { value: 'painting', label: '绘画' },
  { value: 'sculpture', label: '雕塑' },
  { value: 'photography', label: '摄影' },
  { value: 'digital', label: '数字艺术' },
  { value: 'mixed-media', label: '混合媒体' },
  { value: 'drawing', label: '素描' },
  { value: 'printmaking', label: '版画' },
  { value: 'installation', label: '装置艺术' },
  { value: 'other', label: '其他' },
];

const SORT_OPTIONS = [
  { value: 'created_at', label: '最新发布', order: 'DESC' },
  { value: 'likes_count', label: '最受欢迎', order: 'DESC' },
  { value: 'views_count', label: '浏览最多', order: 'DESC' },
  { value: 'price', label: '价格从低到高', order: 'ASC' },
  { value: 'price', label: '价格从高到低', order: 'DESC' },
  { value: 'title', label: '标题 A-Z', order: 'ASC' },
];

export const FilterModal: React.FC<FilterModalProps> = ({
  visible,
  onClose,
  onApply,
  initialFilters = {},
}) => {
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [priceMinText, setPriceMinText] = useState(initialFilters.priceMin?.toString() || '');
  const [priceMaxText, setPriceMaxText] = useState(initialFilters.priceMax?.toString() || '');

  useEffect(() => {
    setFilters(initialFilters);
    setPriceMinText(initialFilters.priceMin?.toString() || '');
    setPriceMaxText(initialFilters.priceMax?.toString() || '');
  }, [initialFilters]);

  const handleApply = () => {
    const finalFilters = { ...filters };
    
    // 处理价格范围
    if (priceMinText) {
      const min = parseFloat(priceMinText);
      if (!isNaN(min) && min >= 0) {
        finalFilters.priceMin = min;
      }
    }
    
    if (priceMaxText) {
      const max = parseFloat(priceMaxText);
      if (!isNaN(max) && max >= 0) {
        finalFilters.priceMax = max;
      }
    }

    onApply(finalFilters);
    onClose();
  };

  const handleReset = () => {
    setFilters({});
    setPriceMinText('');
    setPriceMaxText('');
  };

  const updateFilter = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const selectSortOption = (option: typeof SORT_OPTIONS[0]) => {
    setFilters(prev => ({
      ...prev,
      sortBy: option.value as any,
      sortOrder: option.order as any,
    }));
  };

  const hasActiveFilters = () => {
    return Object.keys(filters).length > 0 || priceMinText || priceMaxText;
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <View style={styles.header}>
            <Text style={styles.title}>筛选条件</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <CloseIcon size={24} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            {/* 分类筛选 */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>作品分类</Text>
              <View style={styles.categoryGrid}>
                {CATEGORIES.map((category) => (
                  <TouchableOpacity
                    key={category.value}
                    style={[
                      styles.categoryItem,
                      filters.category === category.value && styles.categoryItemActive,
                    ]}
                    onPress={() => updateFilter('category', category.value || undefined)}
                  >
                    <Text style={[
                      styles.categoryText,
                      filters.category === category.value && styles.categoryTextActive,
                    ]}>
                      {category.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* 销售状态 */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>销售状态</Text>
              <View style={styles.switchRow}>
                <Text style={styles.switchLabel}>仅显示在售作品</Text>
                <Switch
                  value={filters.isForSale || false}
                  onValueChange={(value) => updateFilter('isForSale', value ? true : undefined)}
                  trackColor={{ false: theme.colors.border, true: theme.colors.primary + '40' }}
                  thumbColor={filters.isForSale ? theme.colors.primary : theme.colors.textSecondary}
                />
              </View>
            </View>

            {/* 价格范围 */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>价格范围 (¥)</Text>
              <View style={styles.priceRow}>
                <TextInput
                  style={styles.priceInput}
                  value={priceMinText}
                  onChangeText={setPriceMinText}
                  placeholder="最低价格"
                  keyboardType="numeric"
                  placeholderTextColor={theme.colors.textSecondary}
                />
                <Text style={styles.priceSeparator}>-</Text>
                <TextInput
                  style={styles.priceInput}
                  value={priceMaxText}
                  onChangeText={setPriceMaxText}
                  placeholder="最高价格"
                  keyboardType="numeric"
                  placeholderTextColor={theme.colors.textSecondary}
                />
              </View>
            </View>

            {/* 创作媒介 */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>创作媒介</Text>
              <TextInput
                style={styles.mediumInput}
                value={filters.medium || ''}
                onChangeText={(value) => updateFilter('medium', value || undefined)}
                placeholder="如：油画、水彩、数字艺术..."
                placeholderTextColor={theme.colors.textSecondary}
              />
            </View>

            {/* 排序方式 */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>排序方式</Text>
              {SORT_OPTIONS.map((option, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.sortOption,
                    filters.sortBy === option.value && 
                    filters.sortOrder === option.order && 
                    styles.sortOptionActive,
                  ]}
                  onPress={() => selectSortOption(option)}
                >
                  <Text style={[
                    styles.sortText,
                    filters.sortBy === option.value && 
                    filters.sortOrder === option.order && 
                    styles.sortTextActive,
                  ]}>
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>

          <View style={styles.footer}>
            <TouchableOpacity
              style={styles.resetButton}
              onPress={handleReset}
            >
              <Text style={styles.resetButtonText}>重置</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.applyButton}
              onPress={handleApply}
            >
              <Text style={styles.applyButtonText}>
                应用筛选{hasActiveFilters() ? ` (${Object.keys(filters).length})` : ''}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modal: {
    backgroundColor: theme.colors.surface,
    borderTopLeftRadius: theme.borderRadius.xl,
    borderTopRightRadius: theme.borderRadius.xl,
    maxHeight: '85%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  title: {
    fontSize: theme.fontSize.xl,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.text,
  },
  closeButton: {
    padding: theme.spacing.xs,
  },
  content: {
    flex: 1,
    padding: theme.spacing.lg,
  },
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    fontSize: theme.fontSize.base,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
  },
  categoryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -theme.spacing.xs,
  },
  categoryItem: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    margin: theme.spacing.xs,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.colors.bg,
  },
  categoryItemActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  categoryText: {
    fontSize: theme.fontSize.sm,
    color: theme.colors.text,
  },
  categoryTextActive: {
    color: 'white',
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  switchLabel: {
    fontSize: theme.fontSize.base,
    color: theme.colors.text,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  priceInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    fontSize: theme.fontSize.base,
    color: theme.colors.text,
    backgroundColor: theme.colors.bg,
  },
  priceSeparator: {
    marginHorizontal: theme.spacing.md,
    fontSize: theme.fontSize.base,
    color: theme.colors.textSecondary,
  },
  mediumInput: {
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    fontSize: theme.fontSize.base,
    color: theme.colors.text,
    backgroundColor: theme.colors.bg,
  },
  sortOption: {
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.xs,
    backgroundColor: theme.colors.bg,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  sortOptionActive: {
    backgroundColor: theme.colors.primary + '20',
    borderColor: theme.colors.primary,
  },
  sortText: {
    fontSize: theme.fontSize.base,
    color: theme.colors.text,
  },
  sortTextActive: {
    color: theme.colors.primary,
    fontWeight: theme.fontWeight.medium,
  },
  footer: {
    flexDirection: 'row',
    padding: theme.spacing.lg,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  resetButton: {
    flex: 1,
    paddingVertical: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
    alignItems: 'center',
    marginRight: theme.spacing.sm,
  },
  resetButtonText: {
    fontSize: theme.fontSize.base,
    color: theme.colors.text,
  },
  applyButton: {
    flex: 2,
    paddingVertical: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.primary,
    alignItems: 'center',
    marginLeft: theme.spacing.sm,
  },
  applyButtonText: {
    fontSize: theme.fontSize.base,
    color: 'white',
    fontWeight: theme.fontWeight.semibold,
  },
});
